import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  applyForexCoupon,
  removeForexCoupon,
  validateForexCoupon,
  type CouponCheck,
} from "service/moneeflo/legacy-forex-desk-api";
import DeskDialog, { Field, inputClass } from "../../components/DeskDialog";
import { formatCurrency } from "utils";

/**
 * A coupon on a sale. Core says whether a code is good for this order and what
 * it takes off, so nothing is worked out here: the code is checked as it is
 * typed, and only Save applies it. A code it refuses comes back with the
 * reason, shown under the field. A flat rupee discount is not offered because
 * core has none; a coupon is the only discount.
 */
export default function DiscountEditDialog({
  couponCode,
  amountBeforeTax,
  customerId,
  disabled = false,
}: {
  couponCode: string;
  /** Rupees: the order's value before tax and charges. */
  amountBeforeTax: number;
  customerId?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [code, setCode] = useState("");
  const [check, setCheck] = useState<CouponCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Ask core as the code is typed, once the typing pauses.
  useEffect(() => {
    setCheck(null);
    setError(null);
    if (!open || code.trim().length < 2 || !(amountBeforeTax > 0)) {
      // Nothing is being checked, so nothing is shown as checking.
      setChecking(false);
      return;
    }
    let current = true;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const result = await validateForexCoupon({ code, amount: amountBeforeTax, customerId });
        if (current) setCheck(result);
      } catch (e) {
        if (current) setError(e instanceof Error ? e.message : "The coupon could not be checked.");
      } finally {
        if (current) setChecking(false);
      }
    }, 400);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [code, open, amountBeforeTax, customerId]);

  const done = () => queryClient.invalidateQueries(["orderDetails"]);
  const apply = useMutation({
    mutationFn: applyForexCoupon,
    onSuccess: () => {
      toast.success("Coupon applied");
      done();
      setOpen(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The coupon could not be applied."),
  });
  const remove = useMutation({
    mutationFn: removeForexCoupon,
    onSuccess: () => {
      toast.success("Coupon removed");
      done();
      setRemoving(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The coupon could not be removed."),
  });

  const hasCoupon = Boolean(couponCode);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setError(null);
          if (hasCoupon) setRemoving(true);
          else {
            setCode("");
            setOpen(true);
          }
        }}
        className="flex cursor-pointer items-center rounded-full bg-[#E6EEFF] px-3 py-1.5 text-sm font-medium text-[#1B4ED7] transition-colors hover:bg-[#D6E4FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#165FE3] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {hasCoupon ? couponCode : "Add Discount"}
        {hasCoupon ? (
          <span aria-hidden className="ml-2 hover:text-red-700">
            ✕
          </span>
        ) : null}
      </button>

      <DeskDialog
        open={open}
        onOpenChange={setOpen}
        title="Discount"
        busy={apply.isLoading}
        submitLabel="Apply"
        submitDisabled={!check?.valid}
        error={error}
        onSubmit={() => {
          setError(null);
          apply.mutate(code);
        }}
      >
        <Field label="Coupon code" htmlFor="coupon-code">
          <input
            id="coupon-code"
            className={inputClass}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Enter coupon code"
            autoComplete="off"
          />
        </Field>
        <p aria-live="polite" className="min-h-[1.25rem] text-sm">
          {checking ? (
            <span className="text-[#666666]">Checking...</span>
          ) : check?.valid ? (
            <span className="text-[#00701D]">
              Takes {formatCurrency(check.discount, "INR")} off the order value.
            </span>
          ) : check ? (
            <span className="text-[#7A1F1F]">{check.reason ?? "This code cannot be used on this order."}</span>
          ) : null}
        </p>
      </DeskDialog>

      <DeskDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Remove coupon"
        description={`Take ${couponCode} off this order?`}
        submitLabel="Remove"
        busy={remove.isLoading}
        error={error}
        onSubmit={() => {
          setError(null);
          remove.mutate();
        }}
      >
        <p className="text-sm text-[#333333]">The discount comes off the total and the amount payable goes back up.</p>
      </DeskDialog>
    </>
  );
}
